import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MultiSelect } from 'primeng/multiselect';
import { Tooltip } from 'primeng/tooltip';
import { Checkbox } from 'primeng/checkbox';
import { catchError, forkJoin, of } from 'rxjs';
import { PresalesService } from '../../services/presales.service';
import { PresalesTaskService } from '../../services/presales-task.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { AuthService } from '../../services/auth.service';
import {
  PresalesProject,
  PresalesTask,
  TrackerCard,
  PresalesProgress,
  PresalesProjectStatus,
  PRESALES_PROGRESS_OPTIONS,
  PRESALES_PROJECT_STATUS_OPTIONS
} from '../../models/presales.model';

/** One column of the board: a progress state and the cards sitting in it. */
interface BoardColumn {
  label: string;
  value: PresalesProgress | 'Not Set';
  cssClass: string;
  cards: TrackerCard[];
}

/**
 * Presales deals and tracker activities on one board, keyed on the Progress state both
 * already share ("To Start", "InProgress", "On Hold", "Closed").
 *
 * <p>A trial screen, added alongside the Inventory and the Activity Tracker rather than
 * replacing either: the question it exists to answer is whether both kinds of work can be
 * read from one board, and that is only answerable by comparing it against the two boards
 * it would eventually replace.</p>
 *
 * <p>Read-only by design. Every card opens the screen that owns its record, so this board
 * can be trialled, changed or dropped without any risk to presales data, and without
 * becoming a second place where the edit forms have to be kept in step. The Activity
 * Tracker carries that history: projects appeared on it once, duplicated the deal board,
 * and had no editor to open, which is why they were taken off again.</p>
 */
@Component({
  selector: 'app-presales-consolidated-board',
  standalone: true,
  imports: [CommonModule, FormsModule, MultiSelect, Tooltip, Checkbox],
  templateUrl: './presales-consolidated-board.component.html',
  styleUrl: './presales-consolidated-board.component.scss'
})
export class PresalesConsolidatedBoardComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private presalesService = inject(PresalesService);
  private taskService = inject(PresalesTaskService);
  private authService = inject(AuthService);
  private router = inject(Router);
  readonly pageHeaderService = inject(PageHeaderSearchActionService);

  loading = false;
  projects: PresalesProject[] = [];
  tasks: PresalesTask[] = [];

  searchText = '';

  selectedOwners: string[] = [];
  selectedStatuses: PresalesProjectStatus[] = [];

  readonly statusOptions = PRESALES_PROJECT_STATUS_OPTIONS;
  readonly progressOptions = PRESALES_PROGRESS_OPTIONS;

  /** An empty column still says "nothing here yet", which is itself worth seeing. */
  hideEmptyColumns = false;

  /**
   * Which card types the board is showing. Both on by default — that is the point of
   * consolidating — but either can be collapsed, so the single-source views these boards
   * offer separately are still reachable from here.
   */
  showProjects = true;
  showActivities = true;

  canManage = false;

  ngOnInit(): void {
    this.pageHeaderService.clearPageConfig();
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search deal or activity...');

    // Same roles that may edit presales work may open it from here.
    this.authService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        this.canManage = !!(user?.isAdmin || user?.isPM);
      });

    this.pageHeaderService.searchText
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(text => {
        this.searchText = text ?? '';
      });

    this.load();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  load(): void {
    this.loading = true;

    // One pass: the board needs both halves before it can lay out a column, so a partial
    // render would only flash an incomplete board.
    forkJoin({
      projects: this.presalesService.getAllProjects().pipe(catchError(() => of([] as PresalesProject[]))),
      tasks: this.taskService.getAll().pipe(catchError(() => of([] as PresalesTask[])))
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ projects, tasks }) => {
        this.projects = projects ?? [];
        this.tasks = tasks ?? [];
        this.loading = false;
      });
  }

  // ---------- board ----------

  /** Deals and activities flattened to one card shape, then filtered. */
  get cards(): TrackerCard[] {
    const search = this.searchText.trim().toLowerCase();

    // A deal's own fields map onto the same card: the client takes `subtitle` (where an
    // activity shows its parent deal), and the Presale PM takes the owner slot.
    // progressPercentage stays null — a pursuit has no numeric completion of its own, and
    // a 0% bar would read as "not started" rather than "not applicable".
    const projectCards: TrackerCard[] = this.showProjects ? this.projects.map(p => ({
      kind: 'project' as const,
      id: p.id,
      name: p.name,
      subtitle: p.presalesClientName,
      clientName: null,
      description: p.description,
      comments: p.comments,
      ownerName: p.managerName,
      progress: p.progress ?? 'To Start',
      status: p.projectStatus ?? null,
      progressPercentage: null,
      startDate: p.startDate ?? null,
      // A deal has no TargetDate; expectedCloseDate is its "when does this land", and is
      // what the overdue treatment keys on.
      targetDate: p.expectedCloseDate ?? null,
      source: p
    })) : [];

    const taskCards: TrackerCard[] = this.showActivities ? this.tasks.map(t => ({
      kind: 'task' as const,
      id: t.id,
      name: t.name,
      subtitle: t.presalesProjectName,
      // Derived server-side from the parent project, so the card needs no extra lookup.
      clientName: t.clientName,
      description: t.description,
      comments: t.comments,
      ownerName: t.ownerName,
      progress: t.progress ?? 'To Start',
      status: t.status ?? null,
      progressPercentage: t.progressPercentage ?? 0,
      startDate: t.startDate ?? null,
      targetDate: t.targetDate ?? null,
      source: t
    })) : [];

    // Deals first within a column: a pursuit is the context its activities hang off, so it
    // reads better above them than interleaved by some shared sort key.
    const all: TrackerCard[] = [...projectCards, ...taskCards];

    return all.filter(card => {
      if (search) {
        // Everything shown on a card is searchable, so what you can read you can find.
        const matches = card.name.toLowerCase().includes(search)
          || (card.subtitle ?? '').toLowerCase().includes(search)
          || (card.clientName ?? '').toLowerCase().includes(search)
          || (card.description ?? '').toLowerCase().includes(search)
          || (card.comments ?? '').toLowerCase().includes(search)
          || (card.ownerName ?? '').toLowerCase().includes(search);
        if (!matches) {
          return false;
        }
      }

      if (this.selectedOwners.length && !this.selectedOwners.includes(card.ownerName ?? '')) {
        return false;
      }

      if (this.selectedStatuses.length
        && !this.selectedStatuses.includes(card.status as PresalesProjectStatus)) {
        return false;
      }

      return true;
    });
  }

  /**
   * Columns keyed on progress. Both sides store the same four values, so no translation is
   * needed — that shared vocabulary is what makes one board possible at all. "Not Set"
   * catches anything carrying a value outside the known list rather than dropping it.
   */
  get columns(): BoardColumn[] {
    const cards = this.cards;

    const known: BoardColumn[] = this.progressOptions.map(option => ({
      label: option.label,
      value: option.value,
      cssClass: this.progressClass(option.value),
      cards: cards.filter(c => c.progress === option.value)
    }));

    const knownValues = this.progressOptions.map(o => o.value);
    const unset = cards.filter(c => !knownValues.includes(c.progress));

    const all = unset.length
      ? [...known, { label: 'Not Set', value: 'Not Set' as const, cssClass: 'progress-unset', cards: unset }]
      : known;

    return this.hideEmptyColumns ? all.filter(c => c.cards.length > 0) : all;
  }

  /** True when neither type is ticked, so an empty board reads as a filter not missing data. */
  get noCardTypeSelected(): boolean {
    return !this.showProjects && !this.showActivities;
  }

  // ---------- counts ----------

  get totalCardCount(): number {
    return this.cards.length;
  }

  /** Deals on the board after filters — half of what the consolidation is being judged on. */
  get projectCardCount(): number {
    return this.cards.filter(c => c.kind === 'project').length;
  }

  get activityCardCount(): number {
    return this.cards.filter(c => c.kind === 'task').length;
  }

  /** Past their date and not yet closed — the count worth acting on. */
  get overdueCardCount(): number {
    return this.cards.filter(c => this.overdueDays(c) !== null).length;
  }

  get unassignedCardCount(): number {
    return this.cards.filter(c => !c.ownerName).length;
  }

  /** Overdue cards within one column, for the subtotal under its title. */
  columnOverdueCount(col: BoardColumn): number {
    return col.cards.filter(c => this.overdueDays(c) !== null).length;
  }

  // ---------- filters ----------

  /** Owners on the board, so the dropdown never offers a name with nothing behind it. */
  get ownerOptions(): { label: string; value: string }[] {
    const names = new Set<string>();
    for (const name of [...this.projects.map(p => p.managerName), ...this.tasks.map(t => t.ownerName)]) {
      if (name) {
        names.add(name);
      }
    }
    return [...names].sort().map(name => ({ label: name, value: name }));
  }

  get hasActiveFilters(): boolean {
    return !!this.searchText.trim()
      || this.selectedOwners.length > 0
      || this.selectedStatuses.length > 0
      || !this.showProjects
      || !this.showActivities;
  }

  clearFilters(): void {
    this.searchText = '';
    this.selectedOwners = [];
    this.selectedStatuses = [];
    this.showProjects = true;
    this.showActivities = true;
    this.pageHeaderService.setSearchText('');
  }

  // ---------- card actions ----------

  /**
   * Opens the clicked record in whichever screen owns it, with its dialog already up:
   * a deal in Presales Project Details, an activity in the Activity Tracker.
   *
   * Details rather than the Inventory for a deal: its dialog carries Overview, Activities,
   * RAID and Team Allocation tabs, so a click from a mixed board lands on the whole pursuit
   * — including its activities — instead of on the Inventory's bare add/edit form.
   *
   * This board edits nothing itself. Both dialogs already exist on those screens, and a
   * second copy here would be another place to keep in step. Passing the id means the click
   * lands on the record rather than on a list the user then has to search.
   */
  onCardClick(card: TrackerCard): void {
    if (!this.canManage) return;

    if (card.kind === 'project') {
      this.router.navigate(['/presales/details'], {
        queryParams: { projectId: card.id, openForm: 1 }
      });
      return;
    }

    this.router.navigate(['/presales/activity-tracker'], {
      queryParams: { taskId: card.id, openForm: 1 }
    });
  }

  // ---------- display helpers ----------

  /** Up to two initials for a card avatar; '?' when the name is missing. */
  initials(name?: string | null): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  }

  /**
   * Deterministic avatar tint from a name. Identical to the other presales boards',
   * deliberately: the same person must get the same colour everywhere, or the tint stops
   * meaning anything.
   */
  avatarHue(name?: string | null): number {
    const s = name || '';
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
    return [210, 150, 275, 25, 340, 190][h % 6];
  }

  /**
   * Column class names, matching the `progress-*` ones the other presales boards use. They
   * have to be exactly these: the shared accent bars and dot colours in _presales.scss key
   * on them.
   */
  progressClass(progress: PresalesProgress | 'Not Set'): string {
    switch (progress) {
      case 'To Start': return 'progress-to-start';
      case 'InProgress': return 'progress-inprogress';
      case 'On Hold': return 'progress-on-hold';
      case 'Closed': return 'progress-closed';
      default: return 'progress-unset';
    }
  }

  /**
   * RAG dot class. Must be the shared `status-badge-*` names: .k-rag in _presales.scss
   * colours on those.
   */
  statusClass(status: PresalesProjectStatus | null | undefined): string {
    switch (status) {
      case 'Green': return 'status-badge-green';
      case 'Amber': return 'status-badge-amber';
      case 'Red': return 'status-badge-red';
      default: return 'status-badge-default';
    }
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '—';
    }
    return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  /** Days a date is past, or null when it is not overdue. Closed work never is. */
  overdueDays(card: TrackerCard): number | null {
    if (!card.targetDate || card.progress === 'Closed') {
      return null;
    }
    const target = new Date(card.targetDate);
    if (Number.isNaN(target.getTime())) {
      return null;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    const days = Math.floor((today.getTime() - target.getTime()) / 86_400_000);
    return days > 0 ? days : null;
  }
}
